<template>
  <div v-if="canViewUi" class="bg-background-neutral-secondary rounded-2xl p-4">
    <div class="flex items-center justify-between">
      <div class="flex items-center space-x-3">
        <div
          class="bg-background-positive-muted flex h-10 w-10 items-center justify-center rounded-xl"
        >
          <CashbackIcon />
        </div>
        <div class="flex flex-col space-y-0.5">
          <div class="flex items-center">
            <div
              v-if="isCustomerLoading || isCustomerRefetching"
              class="shimmer h-6 w-32 rounded-xl"
            />
            <div v-else class="text-text-neutral-primary font-medium">
              {{
                t('loyalty.cashback-amount', {
                  amount: prettyPrice(loyaltyCashback),
                })
              }}
            </div>
            <button type="button" class="px-2 py-1" @click="onOpenInfo">
              <img
                src="@/assets/images/info-icon.svg"
                class="object-contain"
                height="14"
                width="14"
                alt=""
              />
            </button>
          </div>
          <div
            v-if="isApplicationPending || isBillLoading || isBillRefetching"
            class="shimmer h-[18px] w-32 rounded-xl"
          />
          <div v-else class="text-xs">
            <span v-if="isApplied" class="text-text-negative-primary">
              - {{ prettyPrice(appliedAmount) }}
            </span>
            <span v-else class="text-text-neutral-quaternary">
              {{ t('loyalty.cashback-description') }}
            </span>
          </div>
        </div>
      </div>
      <FreeSwitch
        :checked="isToggled"
        :disabled="isDisabled"
        class="duration-150"
        :class="{
          'opacity-50': isDisabled,
        }"
        @onChange="onToggle"
      />
    </div>
    <div
      v-if="loyaltyCashbackDisallowedReason"
      class="text-text-negative-primary mt-2 text-xs"
    >
      {{ loyaltyCashbackDisallowedReason.text }}
    </div>
    <ApplyCashbackInfoBottomSheet />
  </div>
</template>

<script setup lang="ts">
  import { computed, toRef } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { FreeSwitch } from 'freedom-shared';
  import { controllerModel } from 'shared';
  import { prettyPrice } from '@aviata/text-fns';

  import { useLoyaltyCashback } from '@/features/apply-cashback/model';

  import CashbackIcon from '@/assets/images/cashback-icon.svg?component';

  import ApplyCashbackInfoBottomSheet from './ApplyCashbackInfoBottomSheet.vue';

  const props = defineProps<{
    orderId: string;
  }>();

  const emit = defineEmits<{
    onError: [message: string];
  }>();

  const { t } = useI18n();

  const {
    onToggle,
    canViewUi,
    isToggled,
    isApplied,
    isBillLoading,
    isBillRefetching,
    isCustomerLoading,
    isCustomerRefetching,
    isApplicationPending,
    isLoyaltyCashbackAllowed,
    appliedAmount,
    loyaltyCashback,
    loyaltyCashbackDisallowedReason,
  } = useLoyaltyCashback({
    orderId: toRef(props, 'orderId'),
    onError: message => {
      emit('onError', message);
    },
  });

  const isDisabled = computed(
    () =>
      isApplicationPending.value ||
      !isLoyaltyCashbackAllowed.value ||
      isBillLoading.value ||
      isBillRefetching.value,
  );

  const bottomSheet = controllerModel.useBottomSheet();

  function onOpenInfo() {
    bottomSheet.show('apply-cashback-info-bottom-sheet');
  }
</script>
